// Days and clock times, the same in the browser, on the server and in the agent's tools: a real day and a clock time;
// days on and back by the calendar, whatever the clocks do; a moment on a zone's clock; how a time, a length and a day
// read; and the quick add's words ("Lunch with Sam Friday 1pm 1h"), which the Calendar board and voice share.
//
// A day is "YYYY-MM-DD" and a time "HH:MM" on the user's own wall clock, so 9:00 stays 9:00 across a change of the
// clocks. A moment (a calendar's, the board's) is read in the user's own time zone.

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const AT_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** The minutes in a day. */
export const MAX_MINUTES = 24 * 60;

/** A real day, "YYYY-MM-DD", or null. */
export function cleanDay(x: unknown): string | null {
  if (typeof x !== "string") return null;
  const m = DAY_RE.exec(x.trim());
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3] ? x.trim() : null;
}

/** A clock time, "HH:MM" (or "H:MM"), or null. */
export function cleanAt(x: unknown): string | null {
  if (typeof x !== "string") return null;
  const v = x.trim().replace(/^(\d):/, "0$1:");
  return AT_RE.test(v) ? v : null;
}

// ---------------------------------------------------------------------------
// days and clock times

const pad = (n: number) => String(n).padStart(2, "0");

/** The user's day for a Date, from its local parts. */
export function dayOf(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const utcOf = (day: string) => {
  const m = DAY_RE.exec(day)!;
  return Date.UTC(+m[1], +m[2] - 1, +m[3]);
};

/** A day n days on (or back), by the calendar, whatever the clocks do. */
export function addDays(day: string, n: number): string {
  const d = new Date(utcOf(day) + n * 86_400_000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Days from a to b. */
export function daysBetween(a: string, b: string): number {
  return Math.round((utcOf(b) - utcOf(a)) / 86_400_000);
}

/** 0 Sunday to 6 Saturday. */
export function weekdayOf(day: string): number {
  return new Date(utcOf(day)).getUTCDay();
}

/** The first day on that weekday after day (or on it, when same is true). */
export function nextOn(day: string, weekday: number, same = false): string {
  const n = (weekday - weekdayOf(day) + 7) % 7;
  return addDays(day, n === 0 && !same ? 7 : n);
}

/** Minutes after midnight for "HH:MM". */
export function minutesOf(at: string): number {
  const m = AT_RE.exec(at);
  return m ? +m[1] * 60 + +m[2] : 0;
}

/** "HH:MM" for minutes after midnight, kept within the day. */
export function atOf(min: number): string {
  const m = Math.max(0, Math.min(MAX_MINUTES - 1, Math.round(min)));
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** Where a moment falls on the user's clock: their day and the minutes after its midnight. With tz, in that zone. */
/** One formatter for each zone, made once: making one is most of what reading a zone's clock costs (review, 20 Oct). */
const clocks = new Map<string, Intl.DateTimeFormat>();
function clockOf(tz: string): Intl.DateTimeFormat {
  let f = clocks.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    if (clocks.size > 50) clocks.clear();
    clocks.set(tz, f);
  }
  return f;
}

export function wallClock(t: number, tz?: string | null): { day: string; min: number } {
  if (!tz) {
    const d = new Date(t);
    return { day: dayOf(d), min: d.getHours() * 60 + d.getMinutes() };
  }
  try {
    const o: Record<string, number> = {};
    for (const p of clockOf(tz).formatToParts(new Date(t))) if (p.type !== "literal") o[p.type] = Number(p.value);
    return { day: `${o.year}-${pad(o.month)}-${pad(o.day)}`, min: o.hour * 60 + o.minute };
  } catch {
    return wallClock(t, null);
  }
}

/** A time zone the browser names, if it is one this runtime knows. */
export function cleanZone(x: unknown): string | null {
  if (typeof x !== "string" || !x || x.length > 64) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: x });
    return x;
  } catch {
    return null;
  }
}

/** "9 AM", "2:30 PM": a clock time as people say it. */
export function timeLabel(min: number): string {
  const m = ((Math.round(min) % MAX_MINUTES) + MAX_MINUTES) % MAX_MINUTES;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const h12 = h % 12 || 12;
  return `${h12}${mm ? `:${pad(mm)}` : ""} ${h < 12 ? "AM" : "PM"}`;
}

/** "30m", "1h", "1h 30m". */
export function lengthLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function weekdayName(n: number, short = false): string {
  return short ? WEEKDAYS[n].slice(0, 3) : WEEKDAYS[n];
}

/** "Wednesday, October 14" (with the year when it is not this one). */
export function longDay(day: string, today?: string): string {
  const d = new Date(utcOf(day));
  const year = today && today.slice(0, 4) !== day.slice(0, 4) ? `, ${d.getUTCFullYear()}` : "";
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}${year}`;
}

/** "Today", "Tomorrow", "Yesterday", a weekday within the week ahead, else "Fri, Oct 16" (dmy: "Fri 16 Oct", as the UK writes it). */
export function shortDay(day: string, today: string, dmy = false): string {
  const n = daysBetween(today, day);
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n === -1) return "Yesterday";
  const d = new Date(utcOf(day));
  if (n > 1 && n < 7) return WEEKDAYS[d.getUTCDay()];
  if (dmy) return `${WEEKDAYS[d.getUTCDay()].slice(0, 3)} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()].slice(0, 3)}`;
  return `${WEEKDAYS[d.getUTCDay()].slice(0, 3)}, ${MONTHS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCDate()}`;
}

// ---------------------------------------------------------------------------
// the quick add: "Lunch with Sam Friday 1pm 1h"

export interface QuickAdd {
  title: string;
  day: string | null;
  at: string | null;
  minutes: number | null;
  repeat: number | null;
}

export const DAY_WORDS: Record<string, number> = { sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2, wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4, fri: 5, friday: 5, sat: 6, saturday: 6 };
export const MONTH_WORDS: Record<string, number> = { jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11 };
const WD = Object.keys(DAY_WORDS).sort((a, b) => b.length - a.length).join("|");
const MO = Object.keys(MONTH_WORDS).sort((a, b) => b.length - a.length).join("|");

/**
 * Reads what people type in a quick add: its day (today, tomorrow, a weekday, "next Friday", "Oct 20", "2026-10-20"),
 * its time ("2pm", "2:30 pm", "14:00", "at 9", "noon"), its length ("30m", "1h", "1h 30m", "90 min") and
 * "every week" or "every Friday"; the rest is its title. When a part is said twice, the last one counts. A
 * weekday is the next one from today, today included.
 */
export function parseQuick(text: string, today: string): QuickAdd {
  let s = ` ${text.replace(/\s+/g, " ")} `;
  let day: string | null = null;
  let at: string | null = null;
  let minutes: number | null = null;
  let repeat: number | null = null;
  let weekly = false;
  // each part found is cut out of the words, and the last one said counts
  const take = (re: RegExp, f: (m: RegExpExecArray) => boolean) => {
    const hits: RegExpExecArray[] = [];
    for (let m = re.exec(s); m; m = re.exec(s)) hits.push(m);
    for (let i = hits.length - 1; i >= 0; i--) {
      const m = hits[i];
      if (!f(m)) continue;
      s = s.slice(0, m.index) + " " + s.slice(m.index + m[0].length);
      return true;
    }
    return false;
  };
  const g = (src: string) => new RegExp(src, "gi");
  // every week, weekly, every Friday
  take(g(`\\s(?:every|each)\\s+(${WD})\\b`), (m) => {
    repeat = DAY_WORDS[m[1].toLowerCase()];
    day = nextOn(today, repeat, true);
    return true;
  });
  // "weekly" as the first word is a title's ("Weekly review"), anywhere else it is a repeat
  if (repeat === null && take(g(`\\s(?:every\\s+week|weekly|each\\s+week)\\b`), (m) => m.index > 0)) weekly = true;
  // a day
  if (!day)
    take(g(`\\s(?:on\\s+|by\\s+|due\\s+)?(today|tonight|tomorrow|tmrw|tmr|tomorow)\\b`), (m) => {
      day = /^to(day|night)$/i.test(m[1]) ? today : addDays(today, 1);
      return true;
    });
  if (!day)
    take(g(`\\s(?:on\\s+|by\\s+|due\\s+)?(next\\s+|this\\s+)?(${WD})\\b\\.?`), (m) => {
      const wd = DAY_WORDS[m[2].toLowerCase()];
      day = nextOn(today, wd, !m[1] || /this/i.test(m[1]));
      return true;
    });
  if (!day)
    take(g(`\\s(?:on\\s+|by\\s+|due\\s+)?(?:(${MO})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?|(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MO})\\b\\.?)(?:,?\\s+(\\d{4}))?(?=\\s)`), (m) => {
      const mon = MONTH_WORDS[(m[1] ?? m[4]).toLowerCase()];
      const date = Number(m[2] ?? m[3]);
      const y = m[5] ? Number(m[5]) : Number(today.slice(0, 4));
      let d = cleanDay(`${y}-${pad(mon + 1)}-${pad(date)}`);
      // a date already past this year, with no year said, is next year's
      if (d && !m[5] && d < today) d = cleanDay(`${y + 1}-${pad(mon + 1)}-${pad(date)}`);
      if (!d) return false;
      day = d;
      return true;
    });
  if (!day)
    take(g(`\\s(?:on\\s+)?(\\d{4}-\\d{2}-\\d{2})(?=\\s)`), (m) => {
      const d = cleanDay(m[1]);
      if (!d) return false;
      day = d;
      return true;
    });
  // a length, before the time, so "1h" is never read as one o'clock
  take(g(`\\s(?:for\\s+)?(\\d+(?:\\.\\d+)?)\\s?(?:h|hr|hrs|hour|hours)(?:\\s?(\\d{1,2})\\s?(?:m|min|mins|minutes)?)?(?=\\s)`), (m) => {
    const n = Math.round(Number(m[1]) * 60 + (m[2] ? Number(m[2]) : 0));
    if (!n || n > MAX_MINUTES) return false;
    minutes = n;
    return true;
  }) ||
    take(g(`\\s(?:for\\s+)?(\\d{1,4})\\s?(?:m|min|mins|minute|minutes)(?=\\s)`), (m) => {
      const n = Number(m[1]);
      if (n < 5 || n > MAX_MINUTES) return false;
      minutes = n;
      return true;
    });
  // a time
  take(g(`\\s(?:at\\s+|@\\s?)?(noon|midday|midnight)(?=\\s)`), (m) => {
    at = /midnight/i.test(m[1]) ? "00:00" : "12:00";
    return true;
  }) ||
    take(g(`\\s(?:at\\s+|@\\s?)?(\\d{1,2})(?::(\\d{2}))?\\s?(am|pm|a\\.m\\.|p\\.m\\.|a|p)(?=\\s)`), (m) => {
      const h = Number(m[1]);
      const mm = m[2] ? Number(m[2]) : 0;
      if (h < 1 || h > 12 || mm > 59) return false;
      at = atOf(((h % 12) + (/^p/i.test(m[3]) ? 12 : 0)) * 60 + mm);
      return true;
    }) ||
    take(g(`\\s(?:at\\s+|@\\s?)?([01]?\\d|2[0-3]):([0-5]\\d)(?=\\s)`), (m) => {
      at = atOf(Number(m[1]) * 60 + Number(m[2]));
      return true;
    }) ||
    // "at 9" with no am or pm: the working day's (8 to 11 in the morning, 12 to 7 after noon)
    take(g(`\\s(?:at|@)\\s?(\\d{1,2})(?=\\s)`), (m) => {
      const h = Number(m[1]);
      if (h < 1 || h > 12) return false;
      at = atOf((h >= 8 && h <= 11 ? h : h === 12 ? 12 : h + 12) * 60);
      return true;
    });
  if (weekly && repeat === null) {
    day ??= today;
    repeat = weekdayOf(day);
  }
  // a time with no day said is today's
  if (at && !day) day = today;
  const title = s
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+(?:on|at|by|for|due|every)$/i, "")
    .trim();
  return { title, day, at, minutes, repeat };
}
